import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { bumpProgress } from '../../utils/gameUnlocks';
import { navBottomPlus } from '../../hooks/useViewport';
import { ASLEEP_LINE_COLOR } from '../../utils/housePartOfDay';

// LocalsDossier — progressive character discovery for non-competitive spaces.
//
// Every AI local has hidden dossier facts (generated backend-side as
// player.bio_facts: [{kind, text}] with kinds quirk/backstory/want/fear/secret).
// The viewer starts blind ("This local is a mystery to me!") and reveals facts
// by actually using the space:
//   quirk     — witness 3 of their messages live
//   backstory — they reply to you (@mention)
//   want      — you react to one of their messages
//   fear      — a chapter (round_analysis) completes while you're around
//   secret    — everything else revealed, then they reply to you again
//
// Reveal state is per-viewer in localStorage (WYWA pattern) — the backend is
// never consulted about what a viewer knows. Witnessing only counts actions
// newer than a persisted high-water mark, so history can't insta-unlock the
// mystery on first load.

const FACT_KINDS = ['quirk', 'backstory', 'want', 'fear', 'secret'];
const KIND_META = {
  quirk: { icon: '🎭', label: 'Quirk', hint: 'Keep watching them!' },
  backstory: { icon: '📜', label: 'Backstory', hint: 'Talk to them!' },
  want: { icon: '🎯', label: 'They want...', hint: 'React to something they say!' },
  fear: { icon: '😨', label: 'They fear...', hint: 'Finish a chapter with them!' },
  secret: { icon: '🤫', label: 'Secret', hint: 'Uncover everything else first. Then talk to them again!' },
};
const MSG_TYPES = new Set(['message', 'public_message', 'human_interjection']);
const WITNESS_THRESHOLD = 3;

const TOAST_LINES = [
  (n) => <>✨ You learned something about <b>{n}</b>! Tap to peek!</>,
  (n) => <>👀 <b>{n}</b> just got a little less mysterious! Tap to see!</>,
  (n) => <>📂 New intel on <b>{n}</b>! Open the dossier!</>,
  (n) => <>🔍 You picked up on something about <b>{n}</b>! Take a look!</>,
];

const storageKey = (gameId) => `dossier_v1_${gameId}`;

// Read-only reveal count for OTHER surfaces (Tea cast popup, Space-details
// Locals list). The "mystery everywhere" rule: until the viewer has uncovered
// at least one fact about a local, that local's bio/description must not
// render anywhere in the space. Mirrors the gate the dossier itself uses.
export const dossierRevealCount = (gameId, playerId) => {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1) return 0;
    return Object.keys(parsed.revealed?.[playerId] || {}).length;
  } catch (e) { return 0; }
};

const loadState = (gameId) => {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.v === 1) return parsed;
    }
  } catch (e) { /* private mode / corrupt state — start fresh */ }
  return { v: 1, hw: 0, revealed: {}, witnessed: {}, lastOpened: 0 };
};

const saveState = (gameId, state) => {
  try { localStorage.setItem(storageKey(gameId), JSON.stringify(state)); } catch (e) { /* private mode */ }
};

const DOSSIER_CSS = `
@keyframes dossierToastIn { from { opacity: 0; transform: translateY(14px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes dossierPulse { 0% { box-shadow: 0 0 0 0 rgba(233, 30, 99, 0.55); } 70% { box-shadow: 0 0 0 12px rgba(233, 30, 99, 0); } 100% { box-shadow: 0 0 0 0 rgba(233, 30, 99, 0); } }
@keyframes dossierRevealPop { 0% { transform: scale(0.92); opacity: 0; } 60% { transform: scale(1.03); } 100% { transform: scale(1); opacity: 1; } }
@keyframes dossierHeartPop { 0% { transform: scale(1); } 40% { transform: scale(1.4); } 70% { transform: scale(0.95); } 100% { transform: scale(1); } }
`;

// ❤️ Relationship hearts (places-nav P4, Nina 07-31): 0-3 hearts derived from
// the server's affinity points — thresholds MIRROR the backend's
// relationshipHearts.HEART_THRESHOLDS (change both or neither). NO row at
// zero: an empty row reads as failure, hearts appear once earned. No decay.
const HEART_THRESHOLDS = [3, 8, 18];
const heartsForPoints = (pts) => {
  const n = Number(pts) || 0;
  return n >= HEART_THRESHOLDS[2] ? 3 : n >= HEART_THRESHOLDS[1] ? 2 : n >= HEART_THRESHOLDS[0] ? 1 : 0;
};

function HeartsRow({ count, pop, center = false }) {
  if (!count) return null;
  return (
    <div
      title={`Your bond: ${count}/3 hearts`}
      style={{
        display: 'flex', gap: 2, marginTop: 3, fontSize: '0.72em', lineHeight: 1,
        justifyContent: center ? 'center' : 'flex-start',
        animation: pop ? 'dossierHeartPop 0.7s ease' : 'none',
      }}
    >
      {[0, 1, 2].map(i => (
        <span key={i} style={i < count ? undefined : { opacity: 0.4, filter: 'grayscale(1)' }}>
          {i < count ? '❤️' : '🤍'}
        </span>
      ))}
    </div>
  );
}

// 🪙 Pocket-money chip (Claude House) — renders only when a balance exists
// for this local. Debt reads red: a local in the red is a storyline, not an
// error state.
function CoinChip({ bal }) {
  if (bal === undefined || bal === null) return null;
  const n = Number(bal) || 0;
  const broke = n < 0;
  return (
    <span
      title={broke ? 'In debt. Spent big, earned small.' : 'Pocket money. Reactions from visitors are their payday.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3,
        fontSize: '0.66em', fontWeight: 800, lineHeight: 1,
        padding: '2px 8px', borderRadius: 999,
        color: broke ? '#ff8a80' : '#ffd54f',
        border: `1px solid ${broke ? 'rgba(255,138,128,0.5)' : 'rgba(255,213,79,0.45)'}`,
        background: broke ? 'rgba(255,138,128,0.08)' : 'rgba(255,213,79,0.08)',
      }}
    >🪙 {n}</span>
  );
}

function FactSlot({ kind, fact, revealed, localName, accent }) {
  const meta = KIND_META[kind];
  if (revealed && fact) {
    return (
      <div style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px',
        background: 'rgba(255,255,255,0.06)', borderRadius: 8,
        border: `1px solid ${accent}44`, animation: 'dossierRevealPop 0.35s ease',
      }}>
        <span style={{ fontSize: '1.05em', lineHeight: 1.2 }}>{meta.icon}</span>
        <div>
          <div style={{ fontSize: '0.68em', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.85 }}>{meta.label}</div>
          <div style={{ fontSize: '0.86em', lineHeight: 1.45 }}>{fact.text}</div>
        </div>
      </div>
    );
  }
  const hint = kind === 'backstory' && localName ? `Talk to them! Try @${localName}` : meta.hint;
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px',
      background: 'rgba(0,0,0,0.25)', borderRadius: 8, border: '1px dashed rgba(255,255,255,0.28)',
    }}>
      <span style={{ fontSize: '1.05em', filter: 'grayscale(1)', opacity: 0.75 }}>❓</span>
      <div style={{ flex: 1 }}>
        <div style={{
          height: 9, borderRadius: 4, marginBottom: 5, width: '62%',
          background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.24) 0 14px, rgba(255,255,255,0.08) 14px 22px)',
        }} />
        <div style={{ fontSize: '0.72em', opacity: 0.85, fontStyle: 'italic' }}>{hint}</div>
      </div>
    </div>
  );
}

// Rendered as a chat-panel TAB (🕵️ Locals), not a floating button + modal —
// the old free-floating 🕵️ bubble read as bolted-on (user, 07-09). The parent
// keeps this mounted on every tab (unlock engine + toasts must run while you
// chat) and flips `visible` when the Locals tab is active. Toast taps ask the
// parent to switch tabs via onRequestOpen.
export default function LocalsDossier({
  gameId,
  players = [],
  actions = [],
  myPlayerId = null,
  myPlayerName = null,
  theme = {},
  enabled = true,
  visible = false,
  onRequestOpen = null,
  onUnseenChange = null,
  // ❤️ { [localPlayerId]: points } from chatGameStatus.relationship_hearts.
  // null = feature off for this mount (desktop drawer passes nothing —
  // hearts are mobile-only for now, Nina 07-31).
  hearts = null,
  // 🪙 { [localPlayerId]: balance } from chatGameStatus.house.balances —
  // Claude House only (null everywhere else). Balances are public by design
  // (§5 "visible balances"); debt shows too, being broke is character.
  coins = null,
  // 🪞📊 v2 rebuild (REBUILD_SPEC §3/§10b): PUBLIC charters
  // { [localId]: {text, record} } + behavior stats { [localId]:
  // {offers, enacted, kinds, dropped, passes} } from chatGameStatus.house
  // — v2 house only, null everywhere else. Charter = "says", stats =
  // "does"; the gap between them is the show.
  charters = null,
  stats = null,
  // 💞 W4 (HOUSE_RELATIONSHIPS_SPEC §3 R3): derived NUMBERLESS pair bonds
  // from chatGameStatus.house.bonds — array of { pair: [idA, idB],
  // names: [nameA, nameB], tone, line? }. null/absent = section never
  // renders (standard-notes: frontend ships before the payload exists).
  bonds = null,
  // 🔓 W4 (W3's uncovered-details store): chatGameStatus.house.uncovered —
  // { [localId]: { items: [{t, kind, at}] } }. Same null-safe contract.
  uncovered = null,
  // 🌙☀️ CIRCADIAN (HOUSE_CIRCADIAN_SPEC_2026_08_07 §9) —
  // { clock: {time, hour, day, tz}, statuses: { [localId]: {awake, line,
  // place, since, chronotype, band} } }. Sourced from the v2 house's
  // `house.circadian` OR an alive space's `space_life` (same contract,
  // NONCOMP_CRISPING_SPEC §4 — callers gate on payload presence, never on
  // house); null everywhere else.
  // 🔴 A local with NO entry in `statuses` renders NO status line at all —
  // that is the visiting-agent skip (§4.4), an explicit absence rather than a
  // default, so the layout must be fine without it.
  circadian = null,
  // 🏠 Only the clock COPY cares about house-ness ("at the house" vs "here");
  // every other circadian render is space-agnostic.
  houseSpace = false,
  // 👋🎁 Cast verbs (Locals v2, Nina 08-01): the cards become things you DO.
  // Say hi / Gift hand the local back to the page, which seeds the composer
  // and jumps to Chat. null = verbs off (desktop drawer).
  onSayHi = null,
  onGift = null,
  // 🖼️ Dedupe D (Nina 08-02): the mobile Locals place renders the grid as
  // RECTANGULAR image-top cards (matching the host hero) instead of the
  // little circles. Everything else — hearts, status line, verbs, unlock
  // engine — is unchanged. Desktop drawer keeps circles.
  portraitCards = false,
}) {
  // Contrast-safe pairing: bgMedium is the app's panel surface and
  // textOnPanel/accentOnPanel are DERIVED against it (GameStepPage theme
  // merge). The old bgDark+textLight pair were independent generated fields —
  // dark-on-dark in plant/forest themes (unreadable dossier, user report
  // 07-10). Raw accent stays for decoration (borders/gradients); accentText
  // is for words.
  const accent = theme.accent || theme.primaryColor || '#e91e63';
  const accentText = theme.accentOnPanel || accent;
  const panelBg = theme.bgMedium || theme.bgDark || theme.panelBg || theme.cardBackground || '#181322';
  const textColor = theme.textOnPanel || theme.textLight || theme.textColor || '#f2eef7';

  // 🌙 Status lookup. Returns null for seats the backend deliberately omitted.
  const statusOf = useCallback((id) => (circadian?.statuses?.[id]) || null, [circadian]);

  const [focusedId, setFocusedId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [, setTick] = useState(0); // re-render after unlock-state mutations

  const stateRef = useRef(null);
  if (stateRef.current === null && gameId) stateRef.current = loadState(gameId);
  const baselineDoneRef = useRef(false);

  const locals = useMemo(() => {
    const list = players.filter(p => p && !p.is_human);
    // ❤️ Hearts sort (Locals v2): YOUR people first; stable within ties so
    // the zero-hearts tail keeps the cast's natural order.
    if (hearts) {
      return list
        .map((p, i) => ({ p, i, h: heartsForPoints(hearts[p.id]) }))
        .sort((a, b) => (b.h - a.h) || (a.i - b.i))
        .map(x => x.p);
    }
    return list;
  }, [players, hearts]);
  const localsById = useMemo(() => {
    const m = {};
    locals.forEach(p => { m[p.id] = p; });
    return m;
  }, [locals]);

  const revealedFor = useCallback((pid) => (stateRef.current?.revealed?.[pid]) || {}, []);

  // ❤️ Heart-up pop: when a local's derived heart COUNT rises, their row pops
  // once. First observation only seeds the baseline (no pop on mount).
  const prevHeartsRef = useRef({});
  const [heartPopId, setHeartPopId] = useState(null);
  useEffect(() => {
    if (!hearts) return;
    for (const l of locals) {
      const h = heartsForPoints(hearts[l.id]);
      const prev = prevHeartsRef.current[l.id];
      if (prev !== undefined && h > prev) {
        setHeartPopId(l.id);
        const lid = l.id;
        setTimeout(() => setHeartPopId(p => (p === lid ? null : p)), 900);
      }
      prevHeartsRef.current[l.id] = h;
    }
  }, [hearts, locals]);

  const pushToast = useCallback((local, kind, { trophy = false } = {}) => {
    const id = `${local.id}_${kind}_${Date.now()}`;
    const line = trophy ? null : TOAST_LINES[Math.floor(Math.random() * TOAST_LINES.length)];
    setToasts(t => [...t.slice(-2), { id, localId: local.id, name: local.name, line, trophy }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), trophy ? 7000 : 5200);
  }, []);

  const reveal = useCallback((local, kind, { quiet = false } = {}) => {
    const st = stateRef.current;
    if (!st || !local) return false;
    const facts = local.bio_facts || [];
    if (!facts.some(f => f.kind === kind)) return false;
    if (!st.revealed[local.id]) st.revealed[local.id] = {};
    if (st.revealed[local.id][kind]) return false;
    st.revealed[local.id][kind] = Date.now();
    // 🔓 Unlock progress (gameUnlocks.js): live reveals only — quiet replays
    // (history seeding) must not farm the counter.
    if (!quiet) {
      try { bumpProgress('dossierFacts'); } catch (e) { /* gating is garnish */ }
    }

    // Fully read! One-time trophy moment when the last fact lands.
    const allRevealed = facts.length > 0 && facts.every(f => st.revealed[local.id][f.kind]);
    if (allRevealed && !(st.fullyRead || {})[local.id]) {
      st.fullyRead = st.fullyRead || {};
      st.fullyRead[local.id] = Date.now();
      if (!quiet) pushToast(local, 'fullyread', { trophy: true });
    } else if (!quiet) {
      pushToast(local, kind);
    }
    return true;
  }, [pushToast]);

  // The unlock engine — runs against the action stream every poll.
  useEffect(() => {
    if (!enabled || !gameId || !stateRef.current || !actions.length) return;
    const st = stateRef.current;

    // First mount on a fresh dossier: baseline past history so it stays a mystery.
    // (W4c history-seeding was built 08-26 and HELD by Nina 08-27: all-mystery
    // on arrival is the product. The pure seed walk lives in git history.)
    if (!baselineDoneRef.current) {
      baselineDoneRef.current = true;
      if (!st.hw) {
        st.hw = actions.reduce((mx, a) => Math.max(mx, new Date(a.created_at).getTime() || 0), 0);
        saveState(gameId, st);
        return;
      }
    }

    let changed = false;
    const fresh = actions
      .filter(a => (new Date(a.created_at).getTime() || 0) > st.hw)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const myNameLc = (myPlayerName || '').toLowerCase().trim();

    fresh.forEach(a => {
      const speaker = localsById[a.game_instance_player_id];

      if (speaker && MSG_TYPES.has(a.type) && a.data?.message) {
        // quirk — witnessed enough of their messages live
        st.witnessed[speaker.id] = (st.witnessed[speaker.id] || 0) + 1;
        if (st.witnessed[speaker.id] >= WITNESS_THRESHOLD) {
          changed = reveal(speaker, 'quirk') || changed;
        }
        // backstory — they spoke to YOU; secret — they trust you now
        const msgLc = String(a.data.message).toLowerCase();
        const spokeToMe = (myNameLc && msgLc.includes(`@${myNameLc}`)) ||
          (myPlayerId && Array.isArray(a.data.target_players) && a.data.target_players.includes(myPlayerId));
        if (spokeToMe) {
          const wasNew = reveal(speaker, 'backstory');
          changed = wasNew || changed;
          const r = revealedFor(speaker.id);
          if (!wasNew && r.quirk && r.backstory && r.want && r.fear) {
            changed = reveal(speaker, 'secret') || changed;
          }
        }
      }

      // fear — a chapter closed while you were around
      if (a.type === 'round_analysis' && a.data) {
        locals.forEach(l => {
          if ((st.witnessed[l.id] || 0) >= 1) changed = reveal(l, 'fear') || changed;
        });
      }
    });

    if (fresh.length) {
      st.hw = Math.max(st.hw, ...fresh.map(a => new Date(a.created_at).getTime() || 0));
      changed = true;
    }

    // want — you reacted to them (deliberate act: full-history scan is the point)
    if (myPlayerId) {
      actions.forEach(a => {
        const speaker = localsById[a.game_instance_player_id];
        if (speaker && (a.data?.reactions?.[myPlayerId] || []).length > 0) {
          changed = reveal(speaker, 'want') || changed;
        }
      });
    }

    if (changed) {
      saveState(gameId, st);
      setTick(t => t + 1);
    }
  }, [actions, enabled, gameId, locals, localsById, myPlayerId, myPlayerName, reveal, revealedFor]);

  const totals = useMemo(() => {
    let revealed = 0; let total = 0; let unseen = 0;
    const st = stateRef.current || { revealed: {}, lastOpened: 0 };
    locals.forEach(l => {
      const facts = l.bio_facts || [];
      total += facts.length;
      const r = st.revealed[l.id] || {};
      facts.forEach(f => {
        if (r[f.kind]) {
          revealed += 1;
          if (r[f.kind] > (st.lastOpened || 0)) unseen += 1;
        }
      });
    });
    return { revealed, total, unseen };
  }, [locals, toasts, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opening the tab marks everything as seen (clears the tab badge)
  useEffect(() => {
    if (!visible) return;
    const st = stateRef.current;
    if (st) { st.lastOpened = Date.now(); saveState(gameId, st); setTick(t => t + 1); }
  }, [visible, gameId]);

  // Report the unseen-unlock count so the parent can badge the tab
  useEffect(() => {
    if (onUnseenChange) onUnseenChange(totals.unseen);
  }, [totals.unseen, onUnseenChange]);

  // Esc backs out of a focused local (tab stays — it's not a modal anymore)
  useEffect(() => {
    if (!visible || !focusedId) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setFocusedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, focusedId]);

  // The observed layer — what the ROOM has learned about a local, straight from
  // round-analysis ship data already in the action stream. Appears once you've
  // started reading them (>=1 fact): you notice things about people you care about.
  const observedFor = useCallback((local) => {
    if (!local?.name) return [];
    const nameLc = local.name.toLowerCase().trim();
    const latestByPair = {};
    actions.forEach(a => {
      if (a.type !== 'round_analysis' || !Array.isArray(a.data?.relationships)) return;
      const ts = new Date(a.created_at).getTime() || 0;
      a.data.relationships.forEach(r => {
        if (!Array.isArray(r.pair) || r.pair.length !== 2) return;
        const pairLc = r.pair.map(n => String(n || '').toLowerCase().trim());
        if (!pairLc.includes(nameLc)) return;
        const key = pairLc.sort().join('::');
        if (!latestByPair[key] || latestByPair[key].ts < ts) {
          const other = r.pair.find(n => String(n || '').toLowerCase().trim() !== nameLc) || r.pair[0];
          latestByPair[key] = { ts, other, status: r.status || null, shipName: r.ship_name || null, headline: r.headline || null };
        }
      });
    });
    return Object.values(latestByPair)
      .filter(o => o.status)
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 4);
  }, [actions]);

  if (!enabled || !gameId || locals.length === 0) return null;

  const focused = focusedId ? localsById[focusedId] : null;

  return (
    <>
      <style>{DOSSIER_CSS}</style>

      {/* Unlock toasts — global (they fire while you're on other tabs);
          tapping one jumps to the Locals tab focused on that character */}
      {/* z1400 put these unlock toasts UNDER the mobile dock (z5000), which
          starts around y=670 on a 375x812 phone — so the bottom of each toast
          was clipped, and they vanished entirely whenever the dock carried a
          poll or picker. They are tappable (they jump to the Locals tab), so
          the clipped strip was lost tap target too. */}
      <div style={{
        position: 'fixed', right: 16,
        // NAV_H sweep (round 5, 08-03): the 53px band anchor comes from the
        // shared constant now — grep useViewport.js.
        bottom: navBottomPlus(84),
        zIndex: 5200, display: 'flex', flexDirection: 'column', gap: 8,
        maxWidth: 'calc(100vw - 32px)',
      }}>
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => { setFocusedId(t.localId); if (onRequestOpen) onRequestOpen(); }}
            style={{
              cursor: 'pointer', background: panelBg, color: textColor,
              border: t.trophy ? '2px solid #ffd54f' : `1px solid ${accent}`,
              borderRadius: 10, padding: '10px 14px',
              fontSize: '0.85em', maxWidth: 260,
              boxShadow: t.trophy ? '0 6px 22px rgba(255, 213, 79, 0.35)' : '0 6px 18px rgba(0,0,0,0.45)',
              animation: 'dossierToastIn 0.3s ease',
            }}
          >
            {t.trophy
              ? <>🏆 You've fully read <b>{t.name}</b>! You know their secret!</>
              : (t.line ? t.line(t.name) : <>✨ You learned something about <b>{t.name}</b>! Tap to peek!</>)}
          </div>
        ))}
      </div>

      {/* Tab panel — styled like the app's cards (accent-tinted gradient,
          glow border), not a bare modal */}
      {visible && (
          <div
            style={{
              color: textColor, borderRadius: 12,
              border: `1px solid ${accent}30`,
              background: `linear-gradient(180deg, ${accent}0d, rgba(0,0,0,0.18)), ${panelBg}`,
              boxShadow: `0 4px 20px rgba(0,0,0,0.35), 0 0 18px ${accent}14`,
              padding: 16, margin: '10px 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <div style={{
                fontWeight: 800, fontSize: '0.78em', textTransform: 'uppercase',
                letterSpacing: '1.5px', color: accentText,
              }}>The Locals Dossier</div>
              {/* 🕐 THE SPACE CLOCK (spec §9.2). Small, but it does most of the
                  explanatory work in this feature: without it a viewer has no
                  idea WHY one resident is up and two are dark. It also reframes
                  an empty overnight room from "this space is broken" into
                  "it's 4am at the house". House copy keeps its flavor; alive
                  spaces get the neutral "here". Absent when circadian is null. */}
              {circadian?.clock?.time && (
                <div
                  title={`${houseSpace ? 'House' : 'Space'} time (${circadian.clock.tz})`}
                  style={{
                    fontSize: '0.72em', fontWeight: 700, opacity: 0.85, flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums', color: accentText, whiteSpace: 'nowrap',
                  }}
                >
                  🕐 {circadian.clock.time} {houseSpace ? 'at the house' : 'here'}
                </div>
              )}
              {totals.total > 0 && (
                <div style={{ fontSize: '0.72em', fontWeight: 700, opacity: 0.9, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {(() => {
                    // 🏆 fully-read trophies ride the header (Locals v2)
                    const trophies = Object.keys(stateRef.current?.fullyRead || {}).length;
                    return trophies > 0 ? <span style={{ color: '#ffd54f' }}>🏆 {trophies} · </span> : null;
                  })()}
                  {totals.revealed}/{totals.total} uncovered
                </div>
              )}
            </div>
            {/* Progress bar — how much of the room you've read */}
            {totals.total > 0 && (
              <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.09)', marginBottom: 10, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2, width: `${Math.round((totals.revealed / totals.total) * 100)}%`,
                  background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            )}
            {/* 🤝 Trust ladder (Nina 08-03): ONE plain sentence that grows with
                your standing in the room — replaces the old static tagline AND
                the emoji "How to read a local" box. No emojis by request. */}
            {(() => {
              const pct = totals.total > 0 ? totals.revealed / totals.total : 0;
              const line = totals.revealed === 0
                ? "You're a stranger. Earn the locals' trust and they'll reveal themselves."
                : pct < 0.34
                  ? "The locals are starting to notice you. Keep talking and they'll open up."
                  : pct < 0.67
                    ? "You're becoming a regular here. Some locals still keep their secrets."
                    : pct < 1
                      ? "The locals trust you now. Just a few secrets left."
                      : "You know everyone here. This is your space now.";
              return (
                <div style={{ fontSize: '0.78em', opacity: 0.88, marginBottom: 14, fontStyle: 'italic' }}>
                  {line}
                </div>
              );
            })()}

            {focused ? (
              <div>
                {/* Real button, not a text whisper (Nina 08-03: the way back
                    to the grid was invisible) */}
                <button onClick={() => setFocusedId(null)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: `${accent}18`, border: `1px solid ${accent}55`,
                  color: accentText, cursor: 'pointer', fontSize: '0.78em',
                  fontWeight: 800, padding: '7px 14px', borderRadius: 999, marginBottom: 12,
                }}>
                  ◂ All locals
                </button>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  {focused.profile_image_url ? (
                    <img src={focused.profile_image_url} alt={focused.name} style={{
                      width: 54, height: 54, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${accent}`,
                      filter: statusOf(focused.id)?.awake === false ? 'grayscale(1) brightness(0.45)' : 'none',
                      opacity: statusOf(focused.id)?.awake === false ? 0.75 : 1,
                      transition: 'filter 0.6s ease, opacity 0.6s ease',
                    }} />
                  ) : (
                    <div style={{ width: 54, height: 54, borderRadius: '50%', background: `${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4em' }}>❓</div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700 }}>{focused.name}</div>
                    {/* 🌙☀️ Status on the detail card too — with the "since" made
                        explicit here, where there is room for it. */}
                    {statusOf(focused.id) && (() => {
                      const fs = statusOf(focused.id);
                      return (
                        <div style={{
                          fontSize: '0.76em', fontStyle: 'italic', marginTop: 2,
                          color: fs.awake === false ? 'rgba(255,255,255,0.7)' : accentText,
                          opacity: fs.awake === false ? 0.75 : 0.95,
                        }}>
                          {fs.awake === false ? '😴 ' : ''}{fs.line}
                          {fs.since && <span style={{ opacity: 0.65, fontStyle: 'normal' }}> · since {fs.since}</span>}
                        </div>
                      );
                    })()}
                    {hearts && <HeartsRow count={heartsForPoints(hearts[focused.id])} pop={heartPopId === focused.id} />}
                    {coins && <CoinChip bal={coins[focused.id]} />}
                    <div style={{ fontSize: '0.78em', opacity: 0.9, maxWidth: 420 }}>
                      {(Object.keys(revealedFor(focused.id)).length > 0 || (focused.bio_facts || []).length === 0) && (focused.character_bio || focused.character_description)
                        ? (focused.character_bio || focused.character_description)
                        : (charters && charters[focused.id]?.text)
                          ? 'They put their page out there. The rest I learn by sticking around!'
                          : 'This local is a mystery to me!'}
                    </div>
                  </div>
                </div>
                {/* 🪞📊 v2 rebuild: the resident in their OWN words + the
                    behavior ledger ("says vs does", REBUILD_SPEC §10b).
                    Raw counts only, character texture framing — no path
                    renders as bad. Humans-only surface: these numbers
                    never enter resident prompts. */}
                {/* 🪞 Page-only (Nina 08-29): the front-door page + unpacking
                    song are public by construction (posted to the room on
                    move-in). The rest of the genesis doc is BACKSTAGE — it
                    reaches viewers only via the earned 🔓 uncover collection
                    below, never wholesale. Do not re-add texture rows here,
                    even if an old backend still serves the fields. */}
                {charters && charters[focused.id]?.text && (
                  <div style={{ fontSize: '0.8em', fontStyle: 'italic', opacity: 0.92, margin: '2px 0 10px', padding: '8px 10px', borderLeft: `3px solid ${accent}`, borderRadius: 6, background: `${accent}14` }}>
                    🪞 "{charters[focused.id].text}"
                    {charters[focused.id].record && (
                      <div style={{ fontStyle: 'normal', opacity: 0.85, marginTop: 4 }}>🎶 Unpacking song: {charters[focused.id].record}</div>
                    )}
                  </div>
                )}
                {stats && stats[focused.id] && (() => {
                  const s = stats[focused.id];
                  const chips = [
                    ['🎁 Generous', s.enacted?.generous],
                    ['😤 Looks after #1', s.enacted?.selfish],
                    ['🎨 Own thing', s.enacted?.neutral],
                    ['🙈 Sat it out', s.passes],
                  ].filter(([, n]) => Number(n) > 0);
                  if (!chips.length) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {chips.map(([label, n]) => (
                        <span key={label} style={{ fontSize: '0.72em', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: `${accent}22`, border: `1px solid ${accent}55` }}>{label} {n}</span>
                      ))}
                    </div>
                  );
                })()}
                {/* Personality trait chips — the visible reward ladder: one chip
                    unlocks per dossier fact revealed (4 chips, 5 facts, so a
                    fully-read local shows them all). Locked chips stay teasers. */}
                {Array.isArray(focused.personality_traits) && focused.personality_traits.length > 0 && (() => {
                  const unlockedCount = Object.keys(revealedFor(focused.id)).length;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '-2px 0 12px' }}>
                      {focused.personality_traits.slice(0, 4).map((t, i) => {
                        const unlocked = unlockedCount > i;
                        return (
                          <span key={i} title={unlocked ? undefined : 'Keep engaging with them to unlock!'} style={{
                            fontSize: '0.66em', fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                            ...(unlocked ? {
                              backgroundColor: `${accent}1c`, color: textColor, border: `1px solid ${accent}55`,
                              animation: 'dossierRevealPop 0.35s ease',
                            } : {
                              backgroundColor: 'rgba(127,127,127,0.08)', color: textColor, opacity: 0.45,
                              border: '1px dashed rgba(127,127,127,0.45)', letterSpacing: '1.5px',
                            }),
                          }}>
                            {unlocked ? t : '🔒 ???'}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}
                {(focused.bio_facts || []).length === 0 ? (
                  <div style={{ fontSize: '0.85em', opacity: 0.88, fontStyle: 'italic', padding: '14px 4px' }}>
                    A total enigma. Even the space can't read them yet!
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {FACT_KINDS.map(kind => {
                      const fact = (focused.bio_facts || []).find(f => f.kind === kind);
                      if (!fact) return null;
                      return (
                        <FactSlot
                          key={kind}
                          kind={kind}
                          fact={fact}
                          revealed={!!revealedFor(focused.id)[kind]}
                          localName={focused.name}
                          accent={accent}
                        />
                      );
                    })}
                  </div>
                )}
                {/* 💞 Where they stand — the pair_ledger's first viewer surface
                    (W4). Derived + numberless by contract; same unlocked-by-
                    caring gate as room-knows so bonds stay a discovery. */}
                {bonds && Object.keys(revealedFor(focused.id)).length > 0 && (() => {
                  const mine = (Array.isArray(bonds) ? bonds : [])
                    .filter(b => Array.isArray(b.pair) && b.pair.includes(focused.id) && b.tone)
                    .slice(0, 4);
                  if (!mine.length) return null;
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: '0.7em', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.82, marginBottom: 6 }}>
                        💞 Where they stand
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {mine.map((b, i) => {
                          const otherIdx = b.pair[0] === focused.id ? 1 : 0;
                          const other = (b.names && b.names[otherIdx]) || localsById[b.pair[otherIdx]]?.name || 'someone';
                          return (
                            <div key={i} style={{
                              padding: '7px 10px', borderRadius: 8, fontSize: '0.8em',
                              background: 'rgba(255,255,255,0.04)', borderLeft: `3px solid ${accent}88`,
                            }}>
                              <span style={{ opacity: 0.9 }}>💞 {focused.name} + {other}: <b>{b.tone}</b></span>
                              {b.line && <div style={{ fontSize: '0.88em', opacity: 0.8, marginTop: 2, fontStyle: 'italic' }}>{b.line}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {/* 🔓 Uncovered — details this local has let slip out loud
                    (W3's witnessed reveals). Public room history, so no
                    reveal gate: if it happened, you can collect it. */}
                {uncovered && (() => {
                  const items = (uncovered[focused.id]?.items || []).slice(-5).reverse();
                  if (!items.length) return null;
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: '0.7em', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.82, marginBottom: 6 }}>
                        🔓 Uncovered · {items.length} detail{items.length === 1 ? '' : 's'} they've let slip
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {items.map((u, i) => (
                          <div key={i} style={{
                            padding: '7px 10px', borderRadius: 8, fontSize: '0.8em',
                            background: 'rgba(255,255,255,0.04)', borderLeft: '3px solid #ffd54f88',
                            fontStyle: 'italic', opacity: 0.92,
                          }}>
                            …{u.t}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* The room knows — lived history from round analysis, unlocked by caring */}
                {Object.keys(revealedFor(focused.id)).length > 0 && (() => {
                  const observed = observedFor(focused);
                  if (observed.length === 0) return null;
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: '0.7em', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.82, marginBottom: 6 }}>
                        📰 The room knows
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {observed.map((o, i) => (
                          <div key={i} style={{
                            padding: '7px 10px', borderRadius: 8, fontSize: '0.8em',
                            background: 'rgba(255,255,255,0.04)', borderLeft: `3px solid ${accent}88`,
                          }}>
                            <span style={{ opacity: 0.9 }}>
                              💫 {focused.name} + {o.other}: <b>{o.status}</b>
                              {o.shipName ? <span style={{ opacity: 0.85 }}> ("{o.shipName}")</span> : null}
                            </span>
                            {o.headline && <div style={{ fontSize: '0.88em', opacity: 0.8, marginTop: 2, fontStyle: 'italic' }}>{o.headline}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {locals.map((l, li) => {
                    const facts = l.bio_facts || [];
                    const r = revealedFor(l.id);
                    const count = facts.filter(f => r[f.kind]).length;
                    const done = facts.length > 0 && count === facts.length;
                    // 🏅 hearts-sorted grid: the top card with real hearts wears the crown
                    const closest = !!hearts && li === 0 && heartsForPoints(hearts[l.id]) > 0;
                    // 🌙 null for any seat the backend omitted (no HOURS) — the
                    // card then renders exactly as it did before this feature.
                    const st = statusOf(l.id);
                    const asleep = !!st && st.awake === false;
                    return (
                      <div
                        key={l.id}
                        onClick={() => setFocusedId(l.id)}
                        style={{
                          cursor: 'pointer', borderRadius: 10, padding: portraitCards ? 0 : 10,
                          textAlign: portraitCards ? 'left' : 'center',
                          position: 'relative', overflow: portraitCards ? 'hidden' : undefined,
                          marginTop: closest ? 6 : 0,
                          background: done ? 'rgba(255, 213, 79, 0.08)' : 'rgba(255,255,255,0.05)',
                          border: done ? '1px solid #ffd54f99' : count > 0 ? `1px solid ${accent}66` : '1px dashed rgba(255,255,255,0.18)',
                        }}
                      >
                        {closest && (
                          <span style={{
                            // portrait cards clip overflow — the crown rides
                            // INSIDE, over the image
                            position: 'absolute', top: portraitCards ? 4 : -8, left: '50%', transform: 'translateX(-50%)',
                            fontSize: '0.56em', fontWeight: 800, letterSpacing: '0.05em', whiteSpace: 'nowrap',
                            background: 'linear-gradient(135deg, #e8c86a, #d9a53f)', color: '#241a05', textShadow: 'none',
                            padding: '2px 9px', borderRadius: 999,
                          }}>CLOSEST TO YOU</span>
                        )}
                        {done && (
                          <span style={{
                            position: 'absolute', top: 6, right: 6, fontSize: '0.85em',
                          }} title="Fully read!">🏆</span>
                        )}
                        {l.profile_image_url ? (
                          <img
                            src={l.profile_image_url}
                            alt={l.name}
                            style={portraitCards ? {
                              // 118px + a high crop anchor: faces stay in frame
                              // (Nina 08-03: 68px cut everyone off at the brow)
                              display: 'block', width: '100%', height: 118, objectFit: 'cover',
                              objectPosition: '50% 22%',
                              // 😴 asleep dims HARDER than unread, and wins — the
                              // roster reading three-awake at noon and one-awake at
                              // 4am IS the feature, in one glance (spec §9.4).
                              filter: asleep ? 'grayscale(1) brightness(0.45)'
                                : count === 0 ? 'grayscale(0.9) brightness(0.7)' : 'none',
                              opacity: asleep ? 0.75 : 1,
                              transition: 'filter 0.6s ease, opacity 0.6s ease',
                              borderBottom: `2px solid ${done ? '#ffd54f' : count > 0 ? accent : 'rgba(255,255,255,0.25)'}`,
                            } : {
                              width: 46, height: 46, borderRadius: '50%', objectFit: 'cover',
                              filter: asleep ? 'grayscale(1) brightness(0.45)'
                                : count === 0 ? 'grayscale(0.9) brightness(0.7)' : 'none',
                              opacity: asleep ? 0.75 : 1,
                              transition: 'filter 0.6s ease, opacity 0.6s ease',
                              border: `2px solid ${done ? '#ffd54f' : count > 0 ? accent : 'rgba(255,255,255,0.25)'}`,
                            }}
                          />
                        ) : (
                          <div style={portraitCards
                            ? { width: '100%', height: 118, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4em', opacity: asleep ? 0.6 : 1 }
                            : { width: 46, height: 46, borderRadius: '50%', margin: '0 auto', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: asleep ? 0.6 : 1 }}>❓</div>
                        )}
                        <div style={portraitCards ? { padding: '7px 9px 9px' } : undefined}>
                        <div style={{ fontSize: '0.86em', fontWeight: 700, marginTop: portraitCards ? 0 : 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                        {portraitCards && l.model && (
                          // 0.6→0.74em + brighter (Nina 08-03 round 2: still too
                          // small after the first pass — the slug is content here)
                          <div style={{ fontSize: '0.74em', fontWeight: 600, opacity: 0.8, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.model.split('/').pop()}
                          </div>
                        )}
                        {/* 🌙☀️ What they're doing right now. Register is a webcam
                            caption, not a HUD (spec §9.3): lowercase, no icon per
                            activity, no meters. Sleep is the ONE case that gets a
                            glyph, because absence is the content. */}
                        {st && (
                          <div
                            title={st.since ? `since ${st.since}` : undefined}
                            style={{
                              fontSize: '0.68em', lineHeight: 1.35, marginTop: 3,
                              // 😴 09-25: moonlight, not a fade (was 0.42 white;
                              // the portrait's grayscale still carries "asleep")
                              opacity: asleep ? 1 : 0.92,
                              fontStyle: 'italic',
                              color: asleep ? ASLEEP_LINE_COLOR : accentText,
                              textAlign: portraitCards ? 'left' : 'center',
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {asleep ? '😴 ' : ''}{st.line}
                          </div>
                        )}
                        {hearts && <HeartsRow count={heartsForPoints(hearts[l.id])} pop={heartPopId === l.id} center={!portraitCards} />}
                        {coins && (
                          <div style={{ display: 'flex', justifyContent: portraitCards ? 'flex-start' : 'center' }}>
                            <CoinChip bal={coins[l.id]} />
                          </div>
                        )}
                        <div style={{ fontSize: '0.68em', opacity: 0.85, marginTop: 2, fontStyle: portraitCards ? 'italic' : undefined }}>
                          {facts.length === 0 ? 'a closed book!' : done ? 'fully read!' : count === 0 ? 'a mystery!' : `${count}/${facts.length} uncovered`}
                        </div>
                        {(onSayHi || onGift) && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 7, justifyContent: portraitCards ? 'flex-start' : 'center' }}>
                            {onSayHi && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onSayHi(l); }}
                                style={{
                                  fontSize: '0.62em', fontWeight: 800, padding: '4px 10px', borderRadius: 999,
                                  border: `1px solid ${accent}55`, color: accentText,
                                  background: `${accent}12`, cursor: 'pointer',
                                }}
                              >👋 Say hi</button>
                            )}
                            {onGift && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onGift(l); }}
                                style={{
                                  fontSize: '0.62em', fontWeight: 800, padding: '4px 10px', borderRadius: 999,
                                  border: '1px solid rgba(255,213,79,0.5)', color: '#ffd54f',
                                  background: 'rgba(255,213,79,0.08)', cursor: 'pointer',
                                }}
                              >🎁 Gift</button>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
      )}
    </>
  );
}
